import api from './api.service';

export interface K8sCluster {
  id: string;
  name: string;
  region: string;
  provider: string;
  node_count: number;
  pod_count: number;
  namespace_count: number;
  status: 'healthy' | 'warning' | 'critical' | 'unknown';
}

export interface K8sNamespace {
  id: string;
  cluster_id: string;
  name: string;
  status: 'active' | 'terminating';
  pod_count: number;
  created_at: string;
}

export interface K8sNode {
  id: string;
  cluster_id: string;
  name: string;
  status: 'Ready' | 'NotReady' | 'Unknown';
  cpu_capacity?: string;
  memory_capacity?: string;
  cpu_usage_percent?: number;
  memory_usage_percent?: number;
  pod_count: number;
  pods_capacity?: number;
  host_id?: string; // Link to infrastructure host
}

export interface K8sPod {
  id: string;
  cluster_id: string;
  namespace_id: string;
  node_id?: string;
  name: string;
  status: 'Running' | 'Pending' | 'Succeeded' | 'Failed' | 'Unknown' | 'CrashLoopBackOff';
  restart_count: number;
  created_at: string;
  namespace?: K8sNamespace;
  node?: K8sNode;
}

export interface K8sDeployment {
  id: string;
  cluster_id: string;
  namespace_id: string;
  name?: string;
  deployment_name?: string;
  replicas_desired: number;
  replicas_ready: number;
  replicas_up_to_date: number;
  replicas_available: number;
  namespace?: K8sNamespace | string;
}

export interface K8sEvent {
  id: string;
  cluster_id: string;
  event_name: string;
  namespace: string;
  severity: 'info' | 'warning' | 'error';
  reason: string;
  message: string;
  object_kind?: string;
  object_name?: string;
  first_timestamp: string;
  last_timestamp: string;
  count: number;
}

export interface UnhealthyDeployment {
  deployment_name: string;
  namespace: string;
  health: 'healthy' | 'degraded' | 'critical';
  issues: string[];
  replicas_desired: number;
  replicas_ready: number;
}

export interface ServiceWithoutEndpoints {
  service_name: string;
  namespace: string;
  service_type: string;
  endpoint_count: number;
}

export interface HPAAtLimit {
  hpa_name: string;
  namespace: string;
  current_replicas: number;
  max_replicas: number;
  metrics: Array<{
    type: string;
    resource_name?: string;
    target_value?: number;
    current_value?: number;
  }>;
}

export interface ClusterInsights {
  criticalEventsCount: number;
  criticalEvents: K8sEvent[];
  unhealthyDeployments: UnhealthyDeployment[];
  servicesWithoutEndpoints: ServiceWithoutEndpoints[];
  hpasAtLimit: HPAAtLimit[];
  healthScore: number;
}

export interface Recommendation {
  id: string;
  type: 'optimization' | 'security' | 'cost' | 'performance';
  severity: 'low' | 'medium' | 'high' | 'critical';
  title: string;
  description: string;
  resource?: string;
  namespace?: string;
}

export interface ClusterStats {
  totalClusters: number;
  totalNodes: number;
  totalPods: number;
  totalDeployments: number;
  totalServices: number;
  totalNamespaces: number;
  healthyClusters: number;
  criticalClusters: number;
}

export const kubernetesApi = {
  getClusters: (): Promise<K8sCluster[]> => {
    return api.get('/v1/kubernetes/clusters');
  },

  getNamespaces: (clusterId?: string): Promise<K8sNamespace[]> => {
    const params = clusterId ? { clusterId } : {};
    return api.get('/v1/kubernetes/namespaces', { params });
  },

  getNodes: (clusterId?: string): Promise<K8sNode[]> => {
    const params = clusterId ? { clusterId } : {};
    return api.get('/v1/kubernetes/nodes', { params });
  },

  getPods: (namespaceId?: string): Promise<K8sPod[]> => {
    const params = namespaceId ? { namespaceId } : {};
    return api.get('/v1/kubernetes/pods', { params });
  },

  getDeployments: (clusterId?: string): Promise<K8sDeployment[]> => {
    const params = clusterId ? { clusterId } : {};
    return api.get('/v1/kubernetes/deployments', { params });
  },

  getResourceUtilization: (clusterId: string): Promise<any> => {
    return api.get(`/v1/kubernetes/clusters/${clusterId}/utilization`);
  },

  getClusterInsights: (clusterId: string): Promise<ClusterInsights> => {
    return api.get(`/v1/kubernetes/clusters/${clusterId}/insights`);
  },

  getClusterEvents: (
    clusterId: string,
    severity?: 'info' | 'warning' | 'error',
    limit?: number
  ): Promise<K8sEvent[]> => {
    const params: any = {};
    if (severity) params.severity = severity;
    if (limit) params.limit = limit;
    return api.get(`/v1/kubernetes/clusters/${clusterId}/events`, { params });
  },

  getRecommendations: (clusterId: string): Promise<Recommendation[]> => {
    return api.get(`/v1/kubernetes/clusters/${clusterId}/recommendations`);
  },

  getClusterStats: (clusterId: string): Promise<ClusterStats> => {
    return api.get(`/v1/kubernetes/clusters/${clusterId}/stats`);
  },
};
